import { StackScreenProps } from "@react-navigation/stack";
import { ViewInvoiceArrivalRootParamList } from "../../../../types";
import { useInvoiceArrivalContextProvider } from "../../utils/active-invoice-arrival";
import {
  DeletionResponse,
  DeletionResult,
  InvoiceArrivalStatus,
  RetailerInboundResourceType,
  Success,
} from "@towersystems/roam-common/lib/generated-types";
import React from "react";
import {
  Badge,
  FlatActionList,
  Page,
  Stack,
  TileButtonActionList,
  Text,
  Card,
  ProductThumbnail,
  useFlashMessages,
} from "@towersystems/roam-common-native";
import { Spacings, View } from "react-native-ui-lib";
import { formatDate } from "../../../../utilities/date/format-date";
import {
  HTML5_FMT,
  moneyToFormattedString,
} from "@towersystems/roam-common/lib/shared-utils";
import { useMutation } from "@apollo/client";
import {
  MUTATION_DELETE_INVOICE_ARRIVAL,
  SEND_INVOICE_ARRIVAL_INBOUND_RETAILER,
} from "../../utils";
import { ConfirmationPrompt } from "../../../../components/ConfirmationPrompt";
import { useTranslation } from "../../../../utilities/i18n";
import { useGetFormattedInboundRetailerDataForResource } from "../../../../utilities/retailer-inbound";
import { useDownloadJson } from "../../../../utilities/download-json";

export type ViewInvoiceArrivalScreenProps =
  StackScreenProps<ViewInvoiceArrivalRootParamList>;

export const ViewInvoiceArrivalScreen = ({
  navigation,
}: ViewInvoiceArrivalScreenProps) => {
  const { t } = useTranslation("invoice_arrival");
  const { download } = useDownloadJson();
  const { loading: loadingResourceData, getFormattedResourceData } =
    useGetFormattedInboundRetailerDataForResource();

  const [promptVisible, setPromptVisible] = React.useState(false);

  const { invoiceArrival } = useInvoiceArrivalContextProvider();

  const flashMessagesContext = useFlashMessages();

  const [deleteInvoiceMutation, { loading: deletingInvoice }] = useMutation<{
    deleteInvoiceArrival: DeletionResponse;
  }>(MUTATION_DELETE_INVOICE_ARRIVAL);

  const [
    sendInvoiceArrivalInboundRetailer,
    { loading: sendingInvoiceArrival },
  ] = useMutation<{
    sendInvoiceArrivalInboundRetailer: Success;
  }>(SEND_INVOICE_ARRIVAL_INBOUND_RETAILER);

  const handleGetFormattedResourceData = React.useCallback(
    async (stockOrderId: string) => {
      return getFormattedResourceData(
        RetailerInboundResourceType.INVOICE_ARRIVAL,
        stockOrderId
      )
        .then((result) => {
          return download({
            data: result as any,
            fileName: `invoice-arrival__${stockOrderId}.json`,
          });
        })
        .catch((e) => {});
    },
    [loadingResourceData]
  );

  const handleOnDelete = React.useCallback(async () => {
    try {
      const result = await deleteInvoiceMutation({
        variables: {
          id: invoiceArrival.id,
        },
      });

      if (
        result.data?.deleteInvoiceArrival.result &&
        result.data?.deleteInvoiceArrival.result == DeletionResult.DELETED
      ) {
        navigation.goBack();
      } else {
        throw new Error(
          result.data?.deleteInvoiceArrival.reason ?? t("common:error")
        );
      }
    } catch (err: any) {
      flashMessagesContext.showMessage({
        message: err ?? t("common:error"),
        severity: "error",
      });
    }
  }, [invoiceArrival]);

  const handleOnSendInboundRetailer = React.useCallback(async () => {
    sendInvoiceArrivalInboundRetailer({
      variables: {
        id: invoiceArrival.id,
      },
    })
      .then((result) => {
        if (result.data?.sendInvoiceArrivalInboundRetailer.success) {
          flashMessagesContext.showMessage({
            message: t("action.send_retailer.success"),
            severity: "success",
          });
        } else {
          throw new Error(t("action.send_retailer.error"));
        }
      })
      .catch((err: any) => {
        flashMessagesContext.showMessage({
          message: err.message ?? t("common:error"),
          severity: "error",
        });
      });
  }, [invoiceArrival.id]);

  let actions = [];

  if (invoiceArrival.status === InvoiceArrivalStatus.COMPLETE) {
    actions.push({
      isAction: true,
      id: "handleOnSendInboundRetailer",
      label: t("action.send_retailer.label"),
      onPress: handleOnSendInboundRetailer,
      disabled: sendingInvoiceArrival,
    });
    actions.push({
      isAction: true,
      id: "download_json",
      label: t("action.download_json.label"),
      onPress: () => handleGetFormattedResourceData(invoiceArrival.id),
      disabled: loadingResourceData,
      loading: loadingResourceData,
      children: (
        <View style={{ alignItems: "center" }}>
          <Text
            color="subdued"
            textAlign="center"
            style={{ textAlign: "center" }}
          >
            {t("action.download_json.description")}
          </Text>
        </View>
      ),
    });
  }

  if (invoiceArrival.status === InvoiceArrivalStatus.CANCELLED) {
    actions.push({
      isDestructive: true,
      id: "delete",
      label: t("action.delete.label"),
      onPress: () => setPromptVisible(true),
      disabled: false,
    });
  }

  const actionsMarkup =
    actions.length >= 1 ? (
      <View>
        <TileButtonActionList actions={actions} />
      </View>
    ) : null;

  const productsMarkup = invoiceArrival.items.length >= 1 && (
    <Card title="Products">
      <FlatActionList
        style={{ overflow: "visible" }}
        items={invoiceArrival.items.map((item) => ({
          key: item.id,
          titleContent({ subTitle }) {
            return (
              <View flex row>
                <View
                  style={{
                    alignItems: "center",
                    flexBasis: "100%",
                    flexShrink: 1,
                  }}
                  row
                >
                  <ProductThumbnail
                    imageUrl={item.product?.image?.path}
                    quantity={item.quantity}
                    isInternalProduct={false}
                    size="large"
                  />
                  <View marginL-20 style={{ flexShrink: 1 }}>
                    <Text variant="heading2">
                      {item.product?.name ??
                        item.name ??
                        t("common:unknown_resource")}
                    </Text>
                    {subTitle}
                  </View>
                </View>
              </View>
            );
          },
          title: "",
          subTitle: item.barcode,
          componentRight: (icon) => (
            <Stack style={{ marginLeft: Spacings.s4 * 2 }} spacing={1}>
              <View style={{ alignItems: "flex-end" }}>
                <Text variant="heading1" textAlign="right">
                  {moneyToFormattedString(item.totalCost)}
                </Text>
                <Text color="subdued" textAlign="right">
                  {`${moneyToFormattedString(item.costPrice)} EA`}
                </Text>
              </View>
            </Stack>
          ),
        }))}
      ></FlatActionList>
    </Card>
  );

  const totalsMarkup = (
    <Card title={t("card.totals.title")}>
      <FlatActionList
        items={[
          {
            key: "quantity",
            title: t("form.total_quantity.label"),
            componentRight: () => (
              <Text variant="heading1">{invoiceArrival.totalQuantity}</Text>
            ),
          },
          {
            key: "total_gst",
            title: t("form.total_invoice_gst_credit.label"),
            componentRight: () => (
              <Text variant="heading1">
                {moneyToFormattedString(invoiceArrival.formattedGSTCredit)}
              </Text>
            ),
          },
          {
            key: "total",
            title: t("form.total_cost.label"),
            componentRight: () => (
              <Text variant="heading1">
                {moneyToFormattedString(invoiceArrival.totalCost)}
              </Text>
            ),
          },
          {
            key: "total_inc_gst",
            title: t("form.total_cost_inc_gst.label"),
            componentRight: () => (
              <Text variant="heading1">
                {moneyToFormattedString(invoiceArrival.totalCostIncGST)}
              </Text>
            ),
          },
        ]}
      ></FlatActionList>
    </Card>
  );

  const detailsMarkup = (
    <Card title={t("card.details.title")}>
      <FlatActionList
        items={[
          {
            key: "supplier",
            title: t("form.supplier.label"),
            subTitle: t("form.supplier.description"),
            componentRight: () => (
              <Text fontWeight="bold">
                {invoiceArrival.supplier?.name ?? t("common:not_applicable")}
              </Text>
            ),
          },
          {
            key: "location",
            title: t("form.location.label"),
            subTitle: t("form.location.description"),
            componentRight: () => (
              <Text fontWeight="bold">
                {invoiceArrival.location?.name ?? t("common:not_applicable")}
              </Text>
            ),
          },
          {
            key: "invoice_number",
            title: t("form.invoice_number.label"),
            subTitle: t("form.invoice_number.description"),

            componentRight: () => (
              <Text fontWeight="bold">
                {invoiceArrival.invoiceNumber
                  ? t("formatted_invoice_number", {
                      invoice_number: invoiceArrival.invoiceNumber,
                    })
                  : t("common:not_applicable")}
              </Text>
            ),
          },

          {
            key: "invoice_date",
            title: t("form.invoice_date.label"),
            subTitle: t("form.invoice_date.description"),

            componentRight: () => (
              <Text fontWeight="bold">
                {invoiceArrival.invoiceDate
                  ? `${formatDate(invoiceArrival.invoiceDate, HTML5_FMT.DATE)}`
                  : t("commom:not_applicable")}
              </Text>
            ),
          },
          {
            key: "invoiceArrivalDate",
            title: t("form.arrival_date.label"),
            subTitle: t("form.arrival_date.description"),

            componentRight: () => (
              <Text fontWeight="bold">
                {invoiceArrival.arrivalDate
                  ? `${formatDate(invoiceArrival.arrivalDate, HTML5_FMT.DATE)}`
                  : t("common:not_applicable")}
              </Text>
            ),
          },
          {
            key: "invoiceDueDate",
            title: t("form.invoice_due_date.label"),
            subTitle: t("form.invoice_due_date.description"),

            componentRight: () => (
              <Text fontWeight="bold">
                {invoiceArrival.dueDate
                  ? `${formatDate(invoiceArrival.dueDate, HTML5_FMT.DATE)}`
                  : t("common:not_applicable")}
              </Text>
            ),
          },
        ]}
      />
    </Card>
  );
  const deletePrompt = (
    <ConfirmationPrompt
      visible={promptVisible}
      title={t("action.delete_invoice.title")}
      description={t("action.delete_invoice.description")}
      onCancelPress={() => setPromptVisible(false)}
      onConfirmPress={handleOnDelete}
      disabled={deletingInvoice}
    />
  );

  return (
    <Page
      title={t("view_invoice_title")}
      badges={
        <Stack spacing={Spacings.s2}>
          <Badge invoiceArrivalStatus={invoiceArrival.status}></Badge>
        </Stack>
      }
    >
      <Stack vertical>
        {actionsMarkup}
        {detailsMarkup}
        {productsMarkup}
        {totalsMarkup}
      </Stack>
      {deletePrompt}
    </Page>
  );
};
